/**
 * Fælles kontrakt for den interne, cookiefri analyse.
 *
 * Bruges af tre lag, som skal være enige om navne og former:
 *   1. Klient-beacon (components/analytics/SiteAnalytics.tsx) → POST /api/e
 *   2. Collector (app/api/e/route.ts) → Supabase RPC'er (analytics_ingest_*)
 *   3. Admin-siden (/admin/analytics) ← RPC analytics_report(p_from, p_to)
 *
 * Privatliv: ingen cookies, intet i localStorage/sessionStorage, ingen rå IP
 * eller user-agent gemmes. Besøgende genkendes kun via et hash af
 * IP + user-agent + et dagligt salt, som slettes efter to døgn.
 *
 * Ingen "server-only" her — filen importeres også af klient-komponenten.
 */

/** Endpoint for beacon'en. Kort og neutralt navn, så adblock-lister ikke rammer den. */
export const ANALYTICS_ENDPOINT = "/api/e";

/** En session udløber efter 30 min uden aktivitet (samme som GA4). */
export const SESSION_TIMEOUT_MIN = 30;

/** En session tæller som "engageret" (ikke bounce) ved ≥ 10 s aktiv tid. */
export const ENGAGED_MS = 10_000;

// ── Events ──────────────────────────────────────────────────────────────────

export const EVENT_NAMES = [
  "gondrive_click", // klik på "Tilmeld Hold" → videre til GonDrive
  "contact_submit", // kontaktformular sendt (registreres server-side, kun ægte leads)
  "phone_click",    // tel:-link
  "sms_click",      // sms:-link
  "email_click",    // mailto:-link
] as const;
export type EventName = (typeof EVENT_NAMES)[number];

/** Danske labels til admin-siden. */
export const EVENT_LABELS: Record<EventName, string> = {
  gondrive_click: "Tilmeld Hold (GonDrive)",
  contact_submit: "Kontaktformular",
  phone_click: "Opkald",
  sms_click: "SMS",
  email_click: "E-mail",
};

/** Sektionerne på /holdstart — udledt af GonDrive `license_type` (FA er syntetisk for type 193). */
export const HOLD_CATEGORIES = ["Førstehjælp", "Bil", "Motorcykel", "Trailer", "Ukendt"] as const;
export type HoldCategory = (typeof HOLD_CATEGORIES)[number];

/** Hvor på sitet der blev klikket på et hold. */
export const HOLD_PLACEMENTS = ["holdstart", "forside-hero", "forside-liste", "andet"] as const;
export type HoldPlacement = (typeof HOLD_PLACEMENTS)[number];

export const HOLD_PLACEMENT_LABELS: Record<HoldPlacement, string> = {
  holdstart: "Holdstart-siden",
  "forside-hero": "Forsiden – næste hold",
  "forside-liste": "Forsiden – kommende hold",
  andet: "Andre links",
};

/** props for gondrive_click. Alle tekster er de danske API-/visningsnavne (også på /en). */
export type HoldClickProps = {
  category: HoldCategory;
  /** holdName(l) — præcis som på siden, fx "Bil · Aftenhold" eller "Car · English class". */
  hold_name: string;
  /** holdType(l) ("Aftenhold" | "Weekend") eller "English class"; null for dagtimer/førstehjælp. */
  hold_type: string | null;
  /** GonDrive-feltet `title`, fx "Holdstart", "Teori Intro Trailer B/E", "Førstehjælp". */
  api_title: string | null;
  /** GonDrive-feltet `api_text`, fx "Efterårsferie Lynhold". */
  api_text: string | null;
  lesson_id: string | null;
  /** Holdets startdato, "YYYY-MM-DD". */
  lesson_date: string | null;
  placement: HoldPlacement;
  locale: "da" | "en";
};

/** Hvor et tel:/sms:/mailto:-link sad. */
export type ContactArea = "header" | "footer" | "sticky" | "menu" | "side";
export type ContactClickProps = { area: ContactArea };

// ── Beacon-payload (klient → /api/e), sendes som text/plain JSON ─────────────

export type BeaconPageview = {
  t: "pv";
  /** Klient-genereret UUID for sidevisningen — bruges igen af engagement-pings. */
  id: string;
  /** Pathname uden query, fx "/holdstart" eller "/en/priser". */
  p: string;
  /** document.referrer ved første sidevisning i en sideindlæsning; ellers null. */
  r: string | null;
  /** UTM-parametre fra URL'en, hvis til stede. */
  u?: { s?: string; m?: string; c?: string };
  /** 1 hvis URL'en havde gclid/gbraid/wbraid (Google Ads). */
  g?: 1;
  /** 1 hvis URL'en havde fbclid (Meta). */
  f?: 1;
};

export type BeaconEngagement = {
  t: "en";
  /** Sidevisningens id (samme som BeaconPageview.id). */
  id: string;
  /** Aktiv, synlig tid i ms siden sidste ping for denne sidevisning. */
  ms: number;
};

export type BeaconEvent = {
  t: "ev";
  n: Exclude<EventName, "contact_submit">;
  p: string;
  d: HoldClickProps | ContactClickProps;
};

export type BeaconPayload = BeaconPageview | BeaconEngagement | BeaconEvent;

// ── Kanaler (trafikkilder) ───────────────────────────────────────────────────

export const CHANNELS = [
  "Direkte",
  "Google (organisk)",
  "Google Ads",
  "Bing",
  "Andre søgemaskiner",
  "Facebook",
  "Instagram",
  "Meta Ads",
  "Andre sociale medier",
  "AI-assistenter",
  "E-mail",
  "Kampagner (UTM)",
  "Henvisninger",
] as const;
export type Channel = (typeof CHANNELS)[number];

// ── Rapport (RPC analytics_report(p_from date, p_to date) → jsonb) ──────────
// Datoer er kalenderdage i Europe/Copenhagen, begge inklusive.
// Rater er 0–1 (null hvis nævneren er 0). Tider i ms.

export type ReportSummary = {
  visitors: number;
  sessions: number;
  pageviews: number;
  pages_per_session: number | null;
  /** 1 − engagerede sessioner / sessioner. Engageret = ≥2 sidevisninger ELLER ≥1 event ELLER ≥10 s aktiv tid. */
  bounce_rate: number | null;
  /** Gennemsnitlig aktiv tid pr. session. */
  avg_engaged_ms: number | null;
};

export type ReportConversions = Record<EventName, number> & {
  total: number;
  sessions_with_conversion: number;
};

export type ReportDay = { day: string; visitors: number; sessions: number; pageviews: number };

export type ReportPage = {
  path: string;
  pageviews: number;
  visitors: number;
  avg_engaged_ms: number | null;
  /** Antal sessioner der startede på siden. */
  entries: number;
  /** Bouncerate for sessioner der startede på siden. */
  bounce_rate: number | null;
};

export type ReportChannel = {
  channel: Channel | string;
  sessions: number;
  visitors: number;
  bounce_rate: number | null;
  avg_engaged_ms: number | null;
  gondrive_clicks: number;
  contacts: number;
  /** phone_click + sms_click + email_click */
  calls: number;
  /** Sessioner med mindst ét event. */
  converting_sessions: number;
  conversion_rate: number | null;
};

export type ReportReferrer = { host: string; sessions: number; visitors: number };

export type ReportCampaign = {
  source: string | null;
  medium: string | null;
  campaign: string | null;
  sessions: number;
  converting_sessions: number;
};

export type ReportHolds = {
  total: number;
  /** Sessioner med en sidevisning af /holdstart eller /en/holdstart. */
  holdstart_sessions: number;
  /** Klik med placement "holdstart" (dvs. på selve Holdstart-siden). */
  holdstart_clicks: number;
  /** Holdstart-sessioner med mindst ét klik på Holdstart-siden — tæller i klikraten (max 100 %). */
  holdstart_click_sessions: number;
  by_category: { category: HoldCategory | string; clicks: number }[];
  by_hold: { category: HoldCategory | string; hold_name: string; clicks: number }[];
  by_placement: { placement: HoldPlacement | string; clicks: number }[];
  by_lesson: {
    lesson_id: string | null;
    category: HoldCategory | string;
    hold_name: string;
    lesson_date: string | null;
    clicks: number;
  }[];
};

/**
 * Historik importeret fra Vercel Web Analytics (tabellen analytics_history_daily)
 * for dagene FØR egen måling. Indeholder kun besøgende, sidevisninger, sider,
 * henvisende domæner, kanaler (estimat) og kampagner — ikke sessioner, bouncerate,
 * besøgstid, klik eller henvendelser. For historik-dage gælder: besøg ≈ besøgende.
 */
export type ReportHistory = {
  source: "vercel";
  /** Første og sidste dag i HELE den importerede historik. */
  first_day: string;
  last_day: string;
  /** Antal af periodens dage, der bygger på historikken (0 hvis ingen). */
  days: number;
};

export type AnalyticsReport = {
  from: string;
  to: string;
  /** Historik-info; null hvis der ikke findes importeret historik. */
  history: ReportHistory | null;
  /**
   * Første dag med egen måling (dagen for første egne session; historik og egen
   * måling lægges sammen, og den dag kan rumme begge). Uden egne data: dagen efter
   * historikkens sidste dag; null hvis hverken historik eller egne data findes.
   */
  own_from: string | null;
  /**
   * Antal af periodens dage med egen måling. Bouncerate, besøgstid, klik på
   * Tilmeld Hold og henvendelser findes KUN for disse dage — er own_days 0,
   * er de ikke målt (vis "—", ikke 0).
   */
  own_days: number;
  summary: ReportSummary;
  conversions: ReportConversions;
  /** Én række pr. dag i perioden, også dage uden data (nuller). */
  daily: ReportDay[];
  /** Top 50 efter sidevisninger. */
  pages: ReportPage[];
  channels: ReportChannel[];
  /** Top 25 henvisende domæner. */
  referrers: ReportReferrer[];
  /** Top 25 UTM-kombinationer. */
  campaigns: ReportCampaign[];
  holds: ReportHolds;
  /**
   * Tidspunkt for de allerførste data (uanset periode): starten af den importerede
   * historik, ellers første egne session; null hvis ingen data. Egen måling: se own_from.
   */
  first_data_at: string | null;
};
